import { createRef, RefObject, useEffect, useMemo, useState } from 'react';
import { ReactSelectInstanceRefObject, TReactSelectOption } from '../../custom-react-select.types';
import useTrackBoundingClient from '@/utils/use-track-bounding-client';

type Params = {
  tabbedIn: boolean;
  isControlledComponent: boolean;
  autoCalculateViewableItems?: boolean;
  viewableItemsProp?: number;
  selectRef: ReactSelectInstanceRefObject;
  containerRef: RefObject<HTMLDivElement | null>;
  moreItemsRef: RefObject<HTMLDivElement | null>;
  selectedOptions: TReactSelectOption[];
};

const useCalculateViewableItems = ({
  autoCalculateViewableItems,
  viewableItemsProp,
  selectRef,
  containerRef,
  moreItemsRef,
  selectedOptions,
  tabbedIn,
  isControlledComponent,
}: Params) => {
  const [viewableItems, setViewableItems] = useState(autoCalculateViewableItems ? 0 : viewableItemsProp || 0);
  const [recalculateCounter, setRecalculateCounter] = useState(0);

  // trigger recalculation whenever container dimensions change
  const containerRect = useTrackBoundingClient({ ref: containerRef, open: !tabbedIn });
  // create a ref out of the value container to pass into useTrackBoundingClient
  const valueContainerRef = useMemo(() => {
    const _valueContainerRef = createRef<HTMLDivElement | null>();
    _valueContainerRef.current = (selectRef?.current?.controlRef?.firstElementChild || null) as HTMLDivElement | null;
    return _valueContainerRef;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectRef?.current]);
  const valueContainerRect = useTrackBoundingClient({
    ref: valueContainerRef,
    open: !tabbedIn,
  });

  // calculate number of visible items
  useEffect(() => {
    if (tabbedIn) return; // no need to calculate if we're open

    // must be in a setTimeout or the item width won't be accurate
    const calculate = () => {
      const containerEl = containerRef?.current;
      const select = selectRef?.current;
      if (!containerEl || !autoCalculateViewableItems) return;

      if (selectedOptions.length <= 1) {
        setViewableItems(1);
        return;
      }

      const controlEl = select?.controlRef;
      if (!controlEl) return;

      const valueContainer = containerEl.firstElementChild;
      if (!valueContainer) return;

      const valueContainerRect = valueContainer.getBoundingClientRect();
      const valueContainerWidth = valueContainerRect.width;
      const minInputWidth = 4;
      const containerPadding = 8; /* there is no padding-left in unfocused state, so the 8px only represents padding-right */
      const additionalOutlineForControlledComponent = isControlledComponent ? 4 : 0;
      const itemGap = 8 + additionalOutlineForControlledComponent;
      const plusMoreElementWidth = selectedOptions.length > 1 ? moreItemsRef?.current?.offsetWidth || 50 : 0;
      let runningWidth = plusMoreElementWidth + itemGap + minInputWidth + containerPadding;

      const allItems = controlEl.querySelectorAll('.truncated-select-item-container') || [];
      let visibleIndexes = 0;
      for (let i = 0; i < allItems.length; i++) {
        const item = allItems[i];

        if (!(item instanceof HTMLElement)) continue;
        const currentWidthToCheck = (item.offsetWidth || 0) + itemGap;
        if (runningWidth + currentWidthToCheck >= valueContainerWidth) {
          break;
        }
        runningWidth += currentWidthToCheck;
        visibleIndexes = i;
      }

      setViewableItems(visibleIndexes + 1);
    };

    calculate();
  }, [
    tabbedIn,
    autoCalculateViewableItems,
    selectedOptions,
    recalculateCounter,
    containerRef,
    selectRef,
    moreItemsRef,
    containerRect,
    valueContainerRect,
    isControlledComponent,
  ]);

  return {
    viewableItems,
    setViewableItems,
    setRecalculateCounter,
  };
};

export default useCalculateViewableItems;
